import React, { useMemo, useCallback, useState } from 'react';
import '../TeleCalling.css';
import { Box, Typography, Tab, FormControlLabel, Switch, Button, Tooltip } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import AddIcon from '@mui/icons-material/Add';
import AddLinkIcon from '@mui/icons-material/AddLink';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Close';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { handleValidateCodevalue } from '../../../Utility/constant';
import { privateRoutesPath } from '../../../Routes';
import DataGridTable from '../../DataGrid';
import EntityLinkCell from './EntityLinkCell';
import useFilteredCallerData from '../hooks/useFilteredCallerData';
import { useCallContext } from '../useCallContext';
import history from '../../../Utility/history';
import { createRequestData } from '../../../Utility/apiHelper';
import { splitE164 } from '../../../Utility/countryCodes';


const CallerActionableTabs = ({ callerContext, internationalise, minimize }) => {
    const ownerlist = useSelector((state) => state?.owner?.ownerlist);
    const { enqueueSnackbar } = useSnackbar();
    const [pendingTrackTarget, setPendingTrackTarget] = useState(null);
    const {
        selectedCallerTab,
        setSelectedCallerTab,
        callerTabFilters,
        setCallerTabFilters,
        callSession,
        completeTrackedCallSession,
    } = useCallContext();

    const canTrackCall =
        callSession?.lifecycle === 'ended' &&
        callSession?.canTrack &&
        Boolean(callSession?.callSid);

    const activeTabConfig = useMemo(() => ({
        tasks: {
            entityType: 'task',
            createLabel: handleValidateCodevalue(internationalise, 'TASK_CREATE') || 'Create Task',
            createPath: privateRoutesPath.TASKS_CREATE,
        },
        leads: {
            entityType: 'lead',
            createLabel: handleValidateCodevalue(internationalise, 'LEAD_CREATE') || 'Create Lead',
            createPath: privateRoutesPath.CREATE_LEAD,
        },
    }), [internationalise]);

    const handleToggleLeads = useCallback((e) => {
        const { checked } = e.target;
        setCallerTabFilters((prev) => ({ ...prev, showOpenLeads: checked }));
    }, [setCallerTabFilters]);

    const handleToggleTasks = useCallback((e) => {
        const { checked } = e.target;
        setCallerTabFilters((prev) => ({ ...prev, showOpenTasks: checked }));
    }, [setCallerTabFilters]);

    const handleTabChange = useCallback((_, newVal) => {
        setSelectedCallerTab(newVal);
    }, [setSelectedCallerTab]);

    const handleCreateEntity = useCallback(() => {
        const config = activeTabConfig[selectedCallerTab];
        if (!config?.createPath) return;

        const navigationState = {
            source: 'telecalling',
            from: history.location.pathname,
        };

        if (selectedCallerTab === 'leads') {
            const { phoneCountryCode, phone } = splitE164(callSession?.phone || "");
            navigationState.currentLeadDetail = {
                phone,
                phoneCountryCode,
            };
        } else if (selectedCallerTab === 'tasks') {
            const identifiedCustomer = callerContext?.customers?.[0];
            if (identifiedCustomer) {
                navigationState.currentTaskDetail = {
                    id: identifiedCustomer.id,
                };
            }
        }

        history.push({
            pathname: config.createPath,
            state: navigationState,
        });
        minimize && minimize();
    }, [activeTabConfig, callSession?.phone, callerContext?.customers, minimize, selectedCallerTab]);

    const handleTrackCall = useCallback(async (entityType, entityId) => {
        if (!canTrackCall || !entityId) return;

        const url = `${process.env.REACT_APP_BASE_URL}api/telecalling/call-logs/${callSession.callSid}/track`;
        const response = await createRequestData({ entityType, entityId }, url);

        if (response?.status >= 200 && response?.status <= 300) {
    enqueueSnackbar(handleValidateCodevalue(internationalise, 'TC_TRACK_SUCCESSFULLY'),
    { variant: 'success' });
            setPendingTrackTarget(null);
            completeTrackedCallSession();
            return;
        }

        enqueueSnackbar(
            handleValidateCodevalue(internationalise, response?.propererror?.data?.message || 'COMMON_PLEASE_TRY_AGAIN') ||
                'Unable to track call. Please try again.',
            { variant: 'error' }
        );
    }, [callSession?.callSid, canTrackCall, completeTrackedCallSession, enqueueSnackbar, internationalise]);

    const buildTrackColumn = useCallback((entityType) => ({
        field: 'trackOngoingCall',
        type: 'actions',
        headerName: 
           handleValidateCodevalue(internationalise,"TC_TRACK_ONGOING_CALL")
        ,
        width: 100,
        cellClassName: 'actions',
        getActions: ({ id, row }) => {
            const entityId = row?.id || id;
            const targetKey = `${entityType}:${entityId}`;
            const isConfirming = pendingTrackTarget === targetKey;
            const isDisabled = !canTrackCall;

            if (isConfirming) {
                return [
                    <GridActionsCellItem
                        icon={<CheckIcon />}
                        label={handleValidateCodevalue(internationalise, 'COMMON_SAVE') || 'Save'}
                        className="textPrimary"
                        onClick={() => handleTrackCall(entityType, entityId)}
                    />,
                    <GridActionsCellItem
                        icon={<CancelIcon />}
                        label={handleValidateCodevalue(internationalise, 'COMMON_CANCEL') || 'Cancel'}
                        className="textPrimary"
                        onClick={() => setPendingTrackTarget(null)}
                        color="inherit"
                    />,
                ];
            }

            return [
                <GridActionsCellItem
                    icon={<AddLinkIcon fontSize="small" color={isDisabled ? "disabled" : "inherit"} />}
                    label={handleValidateCodevalue(internationalise,"TC_TRACK_ONGOING_CALL")}
                    className={isDisabled ? "" : "textPrimary"}
                    onClick={() => {
                        if (!isDisabled) setPendingTrackTarget(targetKey);
                    }}
                    disabled={isDisabled}
                />,
            ];
        },
    }), [canTrackCall, handleTrackCall, internationalise, pendingTrackTarget]);

    const { displayedLeads, displayedTasks } = useFilteredCallerData(
        callerContext,
        callerTabFilters.showOpenLeads,
        callerTabFilters.showOpenTasks
    );

    const leadColumns = useMemo(() => [
        {
            field: 'id',
            headerName: handleValidateCodevalue(internationalise, 'CUST_EDIT_LEAD_ID') || 'Lead ID',
            flex: 1,
            minWidth: 80,
            editable: false,
            renderCell: ({ id }) => (
                <EntityLinkCell id={id} path={privateRoutesPath.EDIT_LEAD} onNavigate={minimize} />
            ),
        },
        buildTrackColumn('lead'),
        {
            field: 'ownerId',
            headerName: handleValidateCodevalue(internationalise, 'CUST_EDIT_SEARCH_OWNER') || 'Owner',
            flex: 1,
            minWidth: 120,
            editable: false,
            valueGetter: ({ value }) => {
                if (!value) return '';
                const owner = ownerlist?.find((item) => item?.id === value);
                return owner ? `${owner.firstName} ${owner.lastName}` : '';
            },
        },
        {
            field: 'createdAt',
            headerName: handleValidateCodevalue(internationalise, 'CUST_EDIT_LEAD_CREATED_AT') || 'Created',
            flex: 1,
            minWidth: 110,
            editable: false,
        },
        {
            field: 'totalValue',
            headerName: handleValidateCodevalue(internationalise, 'LEAD_SEARCH_TOTAL_VALUE') || 'Value',
            flex: 1,
            minWidth: 90,
            editable: false,
        },
        {
            field: 'status',
            headerName: handleValidateCodevalue(internationalise, 'CUST_EDIT_LEAD_STATUS') || 'Status',
            flex: 1,
            minWidth: 100,
            editable: false,
            valueGetter: ({ value }) => (value ? handleValidateCodevalue(internationalise, value) : ''),
        },
    ], [buildTrackColumn, internationalise, ownerlist, minimize]);

    // ── Task columns ────────────────────────────────────────────────────
    const taskColumns = useMemo(() => [
        {
            field: 'id',
            headerName: handleValidateCodevalue(internationalise, 'CUST_EDIT_TASK_ID') || 'Task ID',
            flex: 1,
            minWidth: 80,
            editable: false,
            renderCell: ({ id }) => (
                <EntityLinkCell id={id} path={privateRoutesPath.EDIT_TASK} onNavigate={minimize} />
            ),
        },
        buildTrackColumn('task'),
        {
            field: 'ownerId',
            headerName: handleValidateCodevalue(internationalise, 'CUST_EDIT_SEARCH_OWNER') || 'Owner',
            flex: 1,
            minWidth: 120,
            editable: false,
            valueGetter: ({ value }) => {
                if (!value) return '';
                const owner = ownerlist?.find((item) => item?.id === value);
                return owner ? `${owner.firstName} ${owner.lastName}` : '';
            },
        },
        {
            field: 'createdAt',
            headerName: handleValidateCodevalue(internationalise, 'CUST_EDIT_TASK_CREATED_AT') || 'Created',
            flex: 1,
            minWidth: 110,
            editable: false,
        },
        {
            field: 'secondaryType',
            headerName: handleValidateCodevalue(internationalise, 'TASK_SECONDARY_TYPE') || 'Type',
            flex: 1,
            minWidth: 100,
            editable: false,
            valueGetter: ({ value }) => (value ? handleValidateCodevalue(internationalise, value) : ''),
        },
        {
            field: 'status',
            headerName: handleValidateCodevalue(internationalise, 'CUST_EDIT_TASK_STATUS') || 'Status',
            flex: 1,
            minWidth: 100,
            editable: false,
            valueGetter: ({ value }) => (value ? handleValidateCodevalue(internationalise, value) : ''),
        },
    ], [buildTrackColumn, internationalise, ownerlist, minimize]);

    return (
        <Box className="tc-tabs-root">
            <TabContext value={selectedCallerTab}>
                {/* Tab bar + active-only toggle */}
                <Box className="tc-tabs-header-row">
                    <TabList
                        onChange={handleTabChange}
                        variant="standard"
                        allowScrollButtonsMobile
                    >
                        <Tab
                            value="tasks"
                            label={`${handleValidateCodevalue(internationalise, 'CUST_EDIT_TASK_DATA') || 'Tasks'} (${displayedTasks.length})`}
                            className="tc-actionable-tab"
                        />
                        <Tab
                            value="leads"
                            label={`${handleValidateCodevalue(internationalise, 'CUST_EDIT_LEAD_DATA') || 'Leads'} (${displayedLeads.length})`}
                            className="tc-actionable-tab"
                        />
                    </TabList>

                    {/* Toggle switch aligned to the right of the tab bar */}
                    <FormControlLabel
                        className="tc-toggle-container"
                        control={
                            <Switch
                                checked={selectedCallerTab === 'leads' ? callerTabFilters.showOpenLeads : callerTabFilters.showOpenTasks}
                                onChange={selectedCallerTab === 'leads' ? handleToggleLeads : handleToggleTasks}
                                name="showOpen"
                                color="primary"
                                size="small"
                            />
                        }
                        label={
                            <Typography variant="caption">
                                {selectedCallerTab === 'leads'
                                    ? (handleValidateCodevalue(internationalise, 'COMMON_SHOW_ONLY_ACTIVE_LEADS') || 'Open only')
                                    : (handleValidateCodevalue(internationalise, 'COMMON_SHOW_ONLY_ACTIVE_TASKS') || 'Open only')
                                }
                            </Typography>
                        }
                    />
                    <Tooltip
                        title={activeTabConfig[selectedCallerTab]?.createLabel}
                        onClick={handleCreateEntity}
                    >
                        <Button
                            className="biztantu-sec-btn-mobile"
                            variant="outlined"
                        >
                            <AddIcon />
                            {activeTabConfig[selectedCallerTab]?.createLabel}
                        </Button>
                    </Tooltip>
                </Box>

                {/* Tasks panel */}
                <TabPanel value="tasks" className="tc-tab-panel">
                    <DataGridTable
                        columns={taskColumns}
                        dataGridData={displayedTasks}
                        isRequiredToolBar={false}
                        isCheckboxReq={false}
                        isInvoiceproduct={true}
                        height="300px"
                    />
                </TabPanel>

                {/* Leads panel */}
                <TabPanel value="leads" className="tc-tab-panel">
                    <DataGridTable
                        columns={leadColumns}
                        dataGridData={displayedLeads}
                        isRequiredToolBar={false}
                        isCheckboxReq={false}
                        isInvoiceproduct={true}
                        height="300px"
                    />
                </TabPanel>
            </TabContext>
        </Box>
    );
};

export default CallerActionableTabs;
